import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nfc2ConvertPanel from "@/components/Nfc2ConvertPanel";
import LoanReturnHistoryPanel from "@/components/LoanReturnHistoryPanel";
import { api } from "@/lib/api";

function fmtDateInput(iso) {
    if (!iso) return "";
    return iso.slice(0, 16); // YYYY-MM-DDTHH:MM
}

function toISO(localStr) {
    if (!localStr) return "";
    // localStr = "YYYY-MM-DDTHH:MM" → add ":00Z" treating as UTC
    return new Date(localStr).toISOString();
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
    // Mostra in orario locale (italiano) — coerente con ciò che l'admin ha inserito
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminMercato() {
    const [windows, setWindows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        name: "",
        kind: "summer",
        season: "25/26",
        start_date: "",
        end_date: "",
        notes: "",
        max_nfc: "",
        max_svi: "",
    });
    const [err, setErr] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get("/market/windows");
            setWindows(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    /* eslint-disable */
    useEffect(() => {
        refresh();
    }, []);
    /* eslint-enable */

    const create = async () => {
        setErr(null);
        if (!form.name || !form.start_date || !form.end_date) {
            setErr("Compila nome, data inizio e data fine");
            return;
        }
        setCreating(true);
        try {
            await api.post("/market/windows", {
                name: form.name,
                kind: form.kind,
                season: form.season,
                start_date: toISO(form.start_date),
                end_date: toISO(form.end_date),
                notes: form.notes || null,
                max_nfc: form.max_nfc === "" ? null : Number(form.max_nfc),
                max_svi: form.max_svi === "" ? null : Number(form.max_svi),
            });
            setForm({ name: "", kind: "summer", season: form.season, start_date: "", end_date: "", notes: "", max_nfc: "", max_svi: "" });
            await refresh();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore creazione finestra");
        } finally {
            setCreating(false);
        }
    };

    const toggleStatus = async (w) => {
        const next = w.status === "open" ? "closed" : "open";
        try {
            await api.patch(`/market/windows/${w.id}`, { status: next });
            await refresh();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore");
        }
    };

    const remove = async (w) => {
        if (!window.confirm(`Eliminare la finestra "${w.name}"?`)) return;
        try {
            await api.delete(`/market/windows/${w.id}`);
            await refresh();
        } catch (e) {
            const detail = e.response?.data?.detail || e.message || "Errore";
            // Se ci sono deal associati, proponi il force delete
            if (detail.includes("affari associati") || detail.includes("force=true")) {
                const ok = window.confirm(
                    `${detail}\n\nVuoi cancellare LA FINESTRA + TUTTI I DEAL?\n` +
                    `I deal accettati verranno annullati (giocatori e budget tornano al venditore).`
                );
                if (!ok) return;
                try {
                    const r = await api.delete(`/market/windows/${w.id}?force=true`);
                    const d = r.data;
                    const reverted = d.reverted_players?.length ? `\nGiocatori riportati: ${d.reverted_players.join(', ')}` : '';
                    const budgets = Object.entries(d.budget_changes || {}).map(([k, v]) => `${k}: ${(v / 1e6).toFixed(1)}M`).join(', ');
                    alert(`Finestra cancellata + ${d.deals_deleted} deal annullati.${reverted}${budgets ? '\nBudget: ' + budgets : ''}`);
                    await refresh();
                } catch (e2) {
                    alert(e2.response?.data?.detail || "Errore force-delete");
                }
            } else {
                alert(detail);
            }
        }
    };

    return (
        <div data-testid="admin-mercato-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// MERCATO</div>
            <h2 className="op-heading text-3xl mt-2 mb-6">Finestre di mercato</h2>

            <MarketSettingsPanel />

            <SeasonSettingsPanel />

            <UglyMaintenancePanel />

            <NfcLoanReturnPanel />
            <Nfc2ConvertPanel />
            <LoanReturnHistoryPanel />

            {/* Create form */}
            <div className="op-card p-5 mb-8" data-testid="create-window-form">
                <div className="op-label mb-3">// NUOVA FINESTRA</div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="op-label block mb-1">Nome</label>
                        <input
                            className="op-input"
                            placeholder="es. Mercato Estivo 25/26"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="window-name"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-1">Tipo</label>
                        <select
                            className="op-input"
                            value={form.kind}
                            onChange={(e) => setForm({ ...form, kind: e.target.value })}
                            data-testid="window-kind"
                        >
                            <option value="summer">Estiva</option>
                            <option value="winter">Invernale</option>
                            <option value="custom">Custom</option>
                        </select>
                    </div>
                    <div>
                        <label className="op-label block mb-1">Stagione</label>
                        <input
                            className="op-input"
                            placeholder="25/26"
                            value={form.season}
                            onChange={(e) => setForm({ ...form, season: e.target.value })}
                            data-testid="window-season"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-1">Inizio</label>
                        <input
                            type="datetime-local"
                            className="op-input"
                            value={form.start_date}
                            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                            data-testid="window-start"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-1">Fine</label>
                        <input
                            type="datetime-local"
                            className="op-input"
                            value={form.end_date}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                            data-testid="window-end"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-1">Max facce nuove (vuoto = limite generale)</label>
                        <input type="number" min="0" className="op-input" value={form.max_nfc}
                            onChange={(e) => setForm({ ...form, max_nfc: e.target.value })} data-testid="window-max-nfc" />
                    </div>
                    <div>
                        <label className="op-label block mb-1">Max svincolati (vuoto = limite generale)</label>
                        <input type="number" min="0" className="op-input" value={form.max_svi}
                            onChange={(e) => setForm({ ...form, max_svi: e.target.value })} data-testid="window-max-svi" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="op-label block mb-1">Note (opzionale)</label>
                        <input
                            className="op-input"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            data-testid="window-notes"
                        />
                    </div>
                </div>
                {err && <div className="text-tff4d4d text-sm mt-3">{err}</div>}
                <div className="mt-4 flex justify-end">
                    <button
                        className="op-btn op-btn-primary"
                        onClick={create}
                        disabled={creating}
                        data-testid="create-window-btn"
                    >
                        {creating ? "Creazione…" : "Apri finestra"}
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="op-label mb-3">// FINESTRE ESISTENTI</div>
            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
            ) : windows.length === 0 ? (
                <div className="op-card p-5 text-t8f95a3 text-sm">Nessuna finestra creata.</div>
            ) : (
                <div className="space-y-2">
                    {windows.map((w) => (
                        <div
                            key={w.id}
                            className="op-card p-4 flex flex-wrap items-center justify-between gap-3"
                            style={{ borderLeft: `3px solid ${w.status === "open" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-22252a))"}` }}
                            data-testid={`window-row-${w.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="op-heading text-xl">{w.name}</div>
                                    <span
                                        className="op-label text-[10px] px-2 py-0.5 border"
                                        style={{
                                            color: w.status === "open" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-8f95a3))",
                                            borderColor: w.status === "open" ? "rgb(var(--tc-2bb6ff) / 0.376)" : "rgb(var(--tc-22252a))",
                                        }}
                                    >
                                        {w.status === "open" ? "ABILITATA" : "CHIUSA"}
                                    </span>
                                    {w.status === "open" && (
                                        w.active_now ? (
                                            <span className="op-label text-[10px] px-2 py-0.5 border border-t00ff66/60 text-t00ff66" data-testid={`window-active-${w.id}`}>
                                                ● APERTA ORA
                                            </span>
                                        ) : (
                                            <span className="op-label text-[10px] px-2 py-0.5 border border-tff7a1a/60 text-tff7a1a" data-testid={`window-scheduled-${w.id}`}>
                                                ⏳ PROGRAMMATA · apre {fmtDateTime(w.start_date)}
                                            </span>
                                        )
                                    )}
                                </div>
                                <div className="op-mono text-xs text-t8f95a3 mt-1">
                                    {w.kind?.toUpperCase()} · {w.season} · {fmtDateTime(w.start_date)} → {fmtDateTime(w.end_date)}
                                </div>
                                {w.notes && <div className="text-xs text-tc9ccd3 mt-1">{w.notes}</div>}
                                <WindowLimits w={w} onSaved={refresh} />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="op-btn"
                                    onClick={() => toggleStatus(w)}
                                    data-testid={`toggle-${w.id}`}
                                >
                                    {w.status === "open" ? "Chiudi" : "Riapri"}
                                </button>
                                <button
                                    className="op-btn"
                                    onClick={() => remove(w)}
                                    data-testid={`delete-${w.id}`}
                                >
                                    Elimina
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function WindowLimits({ w, onSaved }) {
    const [nfc, setNfc] = useState(w.max_nfc ?? "");
    const [svi, setSvi] = useState(w.max_svi ?? "");
    const [season, setSeason] = useState(w.season ?? "");
    const [saving, setSaving] = useState(false);
    const dirty = String(nfc) !== String(w.max_nfc ?? "") || String(svi) !== String(w.max_svi ?? "")
        || String(season) !== String(w.season ?? "");
    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/market/windows/${w.id}`, {
                max_nfc: nfc === "" ? null : Number(nfc),
                max_svi: svi === "" ? null : Number(svi),
                ...(season.trim() && season !== w.season ? { season: season.trim() } : {}),
            });
            await onSaved();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore salvataggio limiti");
        } finally {
            setSaving(false);
        }
    };
    const inp = "op-input !w-20 !py-1 text-sm";
    return (
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-t8f95a3" data-testid={`window-limits-${w.id}`}>
            <span>Stagione</span>
            <input className={inp} value={season} onChange={(e) => setSeason(e.target.value)}
                data-testid={`window-season-${w.id}`} />
            <span>Facce nuove max</span>
            <input type="number" min="0" className={inp} value={nfc} placeholder="gen."
                onChange={(e) => setNfc(e.target.value)} data-testid={`window-limit-nfc-${w.id}`} />
            <span>Svincolati max</span>
            <input type="number" min="0" className={inp} value={svi} placeholder="gen."
                onChange={(e) => setSvi(e.target.value)} data-testid={`window-limit-svi-${w.id}`} />
            {dirty && (
                <button className="op-btn !py-1 text-xs" onClick={save} disabled={saving} data-testid={`window-limits-save-${w.id}`}>
                    {saving ? "…" : "Salva limiti"}
                </button>
            )}
            {w.baseline_at && <span className="text-t5c6370">· contatori azzerati il {fmtDateTime(w.baseline_at)}</span>}
        </div>
    );
}

function MarketSettingsPanel() {
    const [maxNfc, setMaxNfc] = useState(20);
    const [inputValue, setInputValue] = useState("20");
    const [maxSvi, setMaxSvi] = useState(10);
    const [sviInput, setSviInput] = useState("10");
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        api.get("/admin/settings/market").then((r) => {
            setMaxNfc(r.data.max_nfc_per_market);
            setInputValue(String(r.data.max_nfc_per_market));
            setMaxSvi(r.data.max_svi_per_market ?? 10);
            setSviInput(String(r.data.max_svi_per_market ?? 10));
        }).catch(() => {});
    }, []);

    const save = async () => {
        const v = parseInt(inputValue, 10);
        const vSvi = parseInt(sviInput, 10);
        if (isNaN(v) || v < 0 || v > 200 || isNaN(vSvi) || vSvi < 0 || vSvi > 200) {
            alert("Inserisci numeri tra 0 e 200");
            return;
        }
        setSaving(true);
        try {
            const r = await api.put("/admin/settings/market", { max_nfc_per_market: v, max_svi_per_market: vSvi });
            setMaxNfc(r.data.max_nfc_per_market);
            setMaxSvi(r.data.max_svi_per_market ?? 10);
            setSavedAt(new Date());
        } catch (e) {
            alert(e.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const dirty = parseInt(inputValue, 10) !== maxNfc || parseInt(sviInput, 10) !== maxSvi;

    return (
        <div className="op-card p-5 mb-6 border-l-2 border-l-tff7a1a" data-testid="market-settings-panel">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[200px]">
                    <div className="op-label text-tff7a1a">// PARAMETRI MERCATO</div>
                    <h3 className="op-heading text-lg mt-1">Limiti Facce Nuove (NFC) e Svincolati (SVI)</h3>
                    <p className="op-mono text-[11px] text-t8f95a3 mt-2 max-w-2xl leading-relaxed">
                        Numero massimo di NFC e di SVI consentiti per squadra in ogni sessione di mercato.
                        Durante il mercato non c&apos;è blocco: una squadra può acquistare quanti NFC vuole, ma alla chiusura
                        ogni NFC oltre il limite costa <span className="text-tff3b30 font-bold">50mln di multa</span> versata al Bilancio OP.
                    </p>
                </div>
                <div className="flex items-end gap-2">
                    <div>
                        <label className="op-label text-[10px] block mb-1">Max NFC</label>
                        <input
                            type="number"
                            min={0}
                            max={200}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            className="op-input w-24 text-center text-xl op-heading"
                            data-testid="max-nfc-input"
                        />
                    </div>
                    <div>
                        <label className="op-label text-[10px] block mb-1">Max SVI</label>
                        <input
                            type="number"
                            min={0}
                            max={200}
                            value={sviInput}
                            onChange={(e) => setSviInput(e.target.value)}
                            className="op-input w-24 text-center text-xl op-heading"
                            data-testid="max-svi-input"
                        />
                    </div>
                    <button
                        onClick={save}
                        disabled={saving || !dirty}
                        className="op-btn op-btn-primary px-4 py-2 disabled:opacity-50"
                        data-testid="save-max-nfc"
                    >
                        {saving ? "..." : "Salva"}
                    </button>
                </div>
            </div>
            {savedAt && !dirty && (
                <div className="op-mono text-[10px] text-t2bb6ff mt-3" data-testid="saved-feedback">
                    ✓ Salvato {savedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} — limiti attuali {maxNfc} NFC · {maxSvi} SVI
                </div>
            )}
        </div>
    );
}


function SeasonSettingsPanel() {
    const [season, setSeason] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        api.get("/admin/settings/season").then((r) => {
            setSeason(r.data.current_season);
            setInputValue(String(r.data.current_season));
        }).catch(() => {});
    }, []);

    const save = async () => {
        const v = parseInt(inputValue, 10);
        if (isNaN(v) || v < 1 || v > 999) {
            alert("Inserisci un numero di stagione valido (es. 25)");
            return;
        }
        setSaving(true);
        try {
            const r = await api.put("/admin/settings/season", { current_season: v });
            setSeason(r.data.current_season);
            setSavedAt(new Date());
        } catch (e) {
            alert(e.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const dirty = parseInt(inputValue, 10) !== season;

    return (
        <div className="op-card p-5 mb-6 border-l-2 border-l-t2bb6ff" data-testid="season-settings-panel">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[200px]">
                    <div className="op-label text-t2bb6ff">// STAGIONE CORRENTE</div>
                    <h3 className="op-heading text-lg mt-1">Numero stagione</h3>
                    <p className="op-mono text-[11px] text-t8f95a3 mt-2 max-w-2xl leading-relaxed">
                        Usato dall&apos;<span className="text-tffffff font-bold">anti-buyback</span>: un giocatore ceduto a titolo
                        definitivo non può tornare alla squadra di origine nella stagione corrente (X); dalla successiva (X+1) sì.
                        Aggiorna questo numero a ogni passaggio di stagione (es. 25 → 26).
                    </p>
                </div>
                <div className="flex items-end gap-2">
                    <div>
                        <label className="op-label text-[10px] block mb-1">Stagione</label>
                        <input
                            type="number"
                            min={1}
                            max={999}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            className="op-input w-24 text-center text-xl op-heading"
                            data-testid="current-season-input"
                        />
                    </div>
                    <button
                        onClick={save}
                        disabled={saving || !dirty}
                        className="op-btn op-btn-primary px-4 py-2 disabled:opacity-50"
                        data-testid="save-current-season"
                    >
                        {saving ? "..." : "Salva"}
                    </button>
                </div>
            </div>
            {savedAt && !dirty && (
                <div className="op-mono text-[10px] text-t2bb6ff mt-3" data-testid="season-saved-feedback">
                    ✓ Salvato — stagione corrente {season}
                </div>
            )}
        </div>
    );
}



function UglyMaintenancePanel() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    const run = async () => {
        if (!window.confirm("Procedere?\n\n• Marca con status 'Ugly' tutti i giocatori del settore Ugly\n• Promuove in prima squadra (come faccia nuova: NFC, o NFC2 in riparazione) tutti gli Ugly che hanno superato il tetto: età ≥ 22 OPPURE VG autentico ≥ 79\n\nUsa i valori autentici PESDB. Può essere eseguito quante volte vuoi.")) return;
        setBusy(true);
        setErr(null);
        setResult(null);
        try {
            const r = await api.post("/admin/ugly/maintenance");
            setResult(r.data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="op-card p-5 mb-6 border-l-2 border-l-te8ff00" data-testid="ugly-maintenance-panel">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[200px]">
                    <div className="op-label text-te8ff00">// SETTORE UGLY</div>
                    <h3 className="op-heading text-lg mt-1">Manutenzione Ugly</h3>
                    <p className="op-mono text-[11px] text-t8f95a3 mt-2 max-w-2xl leading-relaxed">
                        Sincronizza lo status <span className="text-te8ff00">Ugly</span> su tutti i giocatori del settore giovanile
                        e <strong className="text-tffffff">promuove in prima squadra</strong> chi supera il tetto:
                        <span className="text-te8ff00"> età ≥ 22</span> oppure <span className="text-te8ff00">VG autentico ≥ 79</span> (max 21 anni / 78 VG).
                        Usa i valori <strong className="text-tffffff">autentici PESDB</strong> (utile dopo un aggiornamento del gioco).
                        I promossi ricevono lo status <span className="text-tff7a1a">NFC</span> (faccia nuova). Operazione idempotente.
                    </p>
                </div>
                <button
                    onClick={run}
                    disabled={busy}
                    className="op-btn op-btn-primary px-5 py-2 disabled:opacity-50"
                    data-testid="run-ugly-maintenance"
                >
                    {busy ? "Esecuzione…" : "Esegui manutenzione"}
                </button>
            </div>
            {result && (
                <div className="mt-4 pt-3 border-t border-t22252a" data-testid="ugly-result">
                    <div className="op-mono text-[12px] text-tc9ccd3 mb-2">
                        ✓ <span className="text-te8ff00">{result.status_synced}</span> giocatori Ugly hanno ricevuto lo status
                        · <span className="text-t2bb6ff">{result.promoted}</span> promossi in prima squadra
                        {typeof result.promoted_by_age === "number" && (
                            <span className="text-t8f95a3"> (di cui {result.promoted_by_age} per età 22+, {result.promoted_by_vg} per VG ≥ 79)</span>
                        )}
                    </div>
                    {(result.promoted_names?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {result.promoted_names.map((n) => (
                                <span key={n} className="op-mono text-[10px] px-2 py-0.5 border border-t2bb6ff/40 text-t2bb6ff">
                                    {n}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {err && (
                <div className="mt-3 border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2" data-testid="ugly-error">
                    {err}
                </div>
            )}
        </div>
    );
}


function NfcLoanReturnPanel() {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState(null);
    const [applied, setApplied] = useState(null);

    const applyFix = async () => {
        if (!window.confirm(`Correggere ${preview.count} etichette Faccia Nuova come da elenco?`)) return;
        setBusy(true); setErr(null);
        try {
            const r = await api.post("/market/audit/nfc-window/fix");
            setApplied(r.data);
            const p = await api.get("/market/audit/nfc-window");
            setPreview(p.data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const loadPreview = async () => {
        setBusy(true); setErr(null);
        try {
            const r = await api.get("/market/audit/nfc-window");
            setPreview(r.data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="op-card p-5 mb-6 border-l-2" style={{ borderLeftColor: "rgb(var(--tc-ff7a1a))" }} data-testid="nfc-loan-return-panel">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[200px]">
                    <div className="op-label text-tff7a1a">// FACCE NUOVE (NFC)</div>
                    <h3 className="op-heading text-lg mt-1">Controllo etichette Faccia Nuova</h3>
                    <p className="op-mono text-[11px] text-t8f95a3 mt-2 max-w-2xl leading-relaxed">
                        Regola: chi era in squadra <strong className="text-tffffff">all&apos;inizio della stagione</strong> non è mai Faccia Nuova
                        (anche se è andato via ed è tornato); chi è arrivato dopo sì. Qui trovi le etichette da correggere:
                        solo elenco, nessuna modifica (si correggono a mano dalla scheda).
                    </p>
                </div>
                <button onClick={loadPreview} disabled={busy}
                    className="op-btn px-4 py-2 border border-t2bb6ff/40 text-t2bb6ff disabled:opacity-50"
                    data-testid="nfc-return-preview">
                    {busy ? "…" : "Mostra elenco"}
                </button>
            </div>
            {preview && (
                <div className="mt-4 pt-3 border-t border-t22252a" data-testid="nfc-return-preview-result">
                    {!preview.active ? (
                        <div className="op-mono text-[12px] text-tc9ccd3">Nessuna sessione di mercato in questa stagione: niente da controllare.</div>
                    ) : preview.count === 0 ? (
                        <div className="op-mono text-[12px] text-t7ce38b">✓ Nessun giocatore da sistemare.</div>
                    ) : (
                        <>
                            <div className="op-mono text-[12px] text-tc9ccd3 mb-2">
                                <span className="text-tff7a1a">{preview.count}</span> etichette da correggere:
                            </div>
                            <ul className="space-y-1 max-h-96 overflow-y-auto pr-2">
                                {preview.items.map((it) => (
                                    <li key={it.entry_id} className="op-mono text-[12px]" data-testid={`nfc-missing-${it.entry_id}`}>
                                        <Link to={`/squadre/${it.team_id}`} className="text-tffffff hover:underline">{it.player_name}</Link>
                                        <span className="text-t8f95a3"> — {it.team_name} (a inizio stagione: {it.from_team_name}) → </span>
                                        <span className={it.expected === "nuova" ? "text-tff7a1a" : "text-t7ce38b"} data-testid={`nfc-expected-${it.entry_id}`}>
                                            {it.expected === "nuova" ? "deve essere Faccia Nuova" : "togli Faccia Nuova"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <button onClick={applyFix} disabled={busy}
                                className="op-btn mt-3 px-4 py-2 border border-tff7a1a/50 text-tff7a1a disabled:opacity-50"
                                data-testid="nfc-return-apply">
                                {busy ? "…" : `Correggi tutte (${preview.count})`}
                            </button>
                        </>
                    )}
                    {applied && (
                        <div className="op-mono text-[12px] text-t7ce38b mt-2" data-testid="nfc-return-apply-result">
                            ✓ Fatto: tolte {applied.removed}, aggiunte {applied.added}.
                        </div>
                    )}
                </div>
            )}
            {err && (
                <div className="mt-3 border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2" data-testid="nfc-return-error">
                    {err}
                </div>
            )}
        </div>
    );
}
